import { getCustomPages, saveCustomPage } from '../../../services/ecommerceApi';
import { FAVYOFLEX_COMPANY_ID } from '../../../utils/favyoflexPageHero';

/** Avantajlar sayfası ve menüsündeki alt sayfalar. Sıra vitrin menüsüyle aynı. */
const ADVANTAGE_PAGE_SLUGS = [
  'avantajlar',
  'yemek-karti',
  'hediye-karti',
  'market-karti',
  'esnek-yan-haklar',
  'esnek-yan-hak',
  'giyim-karti',
  'basvurular',
] as const;

const ESNEK_SLUG = 'esnek-yan-haklar';

export interface CustomPageTargetOption {
  value: string;
  label: string;
  group: string;
}

const pageSlug = (page: any): string => String(page?.slug || page?.Slug || '').trim().toLowerCase();

const pageTitle = (page: any): string => String(page?.title || page?.Title || page?.slug || 'Özel Sayfa');

const isActivePage = (page: any): boolean => page?.isActive !== false && page?.IsActive !== false;

export const isFavyoflexAdvantageSlug = (slug?: string | null): boolean =>
  ADVANTAGE_PAGE_SLUGS.includes(String(slug || '').trim().toLowerCase() as (typeof ADVANTAGE_PAGE_SLUGS)[number]);

const advantageRank = (slug: string): number => {
  const index = ADVANTAGE_PAGE_SLUGS.indexOf(slug as (typeof ADVANTAGE_PAGE_SLUGS)[number]);
  return index === -1 ? ADVANTAGE_PAGE_SLUGS.length : index;
};

/** Avantajlar ana sayfası + menüdeki alt sayfalar ayrı grupta, diğer özel sayfalar kendi grubunda. */
export const buildCustomPageTargetOptions = (pages: any[], companyId?: string): CustomPageTargetOption[] => {
  const active = (Array.isArray(pages) ? pages : []).filter(isActivePage);
  const nestAdvantages = String(companyId || '') === FAVYOFLEX_COMPANY_ID;
  const advantage = nestAdvantages
    ? active
        .filter((page) => isFavyoflexAdvantageSlug(pageSlug(page)))
        .sort((a, b) => advantageRank(pageSlug(a)) - advantageRank(pageSlug(b)))
    : [];
  const others = active.filter((page) => !nestAdvantages || !isFavyoflexAdvantageSlug(pageSlug(page)));

  return [
    ...advantage.map((page) => {
      const slug = pageSlug(page);
      const title = pageTitle(page);
      return {
        value: `custom-page:${page.id}`,
        label: slug === 'avantajlar' ? 'Avantajlar sayfası' : title,
        group: 'Avantajlar',
      };
    }),
    ...others.map((page) => ({
      value: `custom-page:${page.id}`,
      label: pageTitle(page),
      group: 'Özel Sayfalar',
    })),
  ];
};

/** Düz sayfa listelerinde Avantajlar alt sayfasını üst sayfasıyla birlikte göster. */
export const customPagePickerLabel = (page: any, companyId?: string): string => {
  const slug = pageSlug(page);
  const title = pageTitle(page);
  if (String(companyId || '') !== FAVYOFLEX_COMPANY_ID) return title;
  if (slug === 'avantajlar') return 'Avantajlar';
  if (isFavyoflexAdvantageSlug(slug)) return `Avantajlar › ${title}`;
  return title;
};

let ensureInflight: Promise<any[] | null> | null = null;

/**
 * FavyoFlex Avantajlar menüsündeki "Esnek Yan Haklar" alt sayfası panelde yoksa oluşturur.
 * Aynı anda birden fazla panel açılırsa tek kayıt yazılır.
 */
export const ensureFavyoflexAdvantageSubpages = async (
  configId: string,
  companyId: string,
  pages: any[]
): Promise<any[]> => {
  const current = Array.isArray(pages) ? pages : [];
  if (String(companyId || '') !== FAVYOFLEX_COMPANY_ID || !configId) return current;
  if (current.some((page) => pageSlug(page) === ESNEK_SLUG || pageSlug(page) === 'esnek-yan-hak')) {
    return current;
  }

  if (!ensureInflight) {
    ensureInflight = (async () => {
      const fresh = await getCustomPages(configId);
      const list = Array.isArray(fresh?.data) ? fresh.data : [];
      if (list.some((page: any) => pageSlug(page) === ESNEK_SLUG || pageSlug(page) === 'esnek-yan-hak')) {
        return list;
      }
      await saveCustomPage({
        configId,
        title: 'Esnek Yan Haklar Platformu',
        slug: ESNEK_SLUG,
        subtitle: 'Tüm yan hakları tek bir dijital platformda yönetin',
        content: '<p>Tüm yan hakları tek bir dijital platformda yönetin.</p>',
        isActive: true,
        showInMenu: false,
        showInFooter: false,
        displayOrder: Math.max(0, ...list.map((page: any) => Number(page?.displayOrder) || 0)) + 1,
      });
      const again = await getCustomPages(configId);
      return Array.isArray(again?.data) ? again.data : list;
    })().finally(() => {
      ensureInflight = null;
    });
  }

  try {
    const next = await ensureInflight;
    return next && next.length > 0 ? next : current;
  } catch (error) {
    console.error('[FAVYOFLEX] Esnek Yan Haklar alt sayfası eklenemedi:', error);
    return current;
  }
};
